// frontend/src/utils/pdfGenerator.js - VERSIÓN MEJORADA CON MEJOR ESPACIADO
import { jsPDF } from 'jspdf';
import { buildCostBreakdown, conceptHasAmount, hasAnyCost } from './costBreakdown';
import { parseDate } from './dates';

/**
 * Convertir valor a string seguro para jsPDF
 */
const toSafeString = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'string') return value;
  return String(value);
};

/**
 * Agregar sección con header azul
 */
const addSection = (doc, title, yPos, pageWidth) => {
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(66, 133, 244);
  doc.rect(15, yPos, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(title, 17, yPos + 5.5);
  doc.setTextColor(0, 0, 0);
  return yPos + 12;
};

/**
 * Agregar campo (label: valor)
 */
const addField = (doc, label, value, yPos, pageWidth, maxWidth = null) => {
  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text(label, 17, yPos);
  
  doc.setFont(undefined, 'normal');
  const valueStr = toSafeString(value);
  const splitWidth = maxWidth || (pageWidth - 80);
  const valueLines = doc.splitTextToSize(valueStr, splitWidth);
  doc.text(valueLines, 65, yPos);
  
  return yPos + Math.max(5, valueLines.length * 5);
};

/**
 * Agregar bloque de texto largo
 */
const addTextBlock = (doc, text, yPos, pageWidth) => {
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  const lines = doc.splitTextToSize(toSafeString(text), pageWidth - 35);
  doc.text(lines, 17, yPos);
  return yPos + (lines.length * 5) + 3;
};

/**
 * Verificar si necesita nueva página
 */
const checkPageBreak = (doc, yPos, spaceNeeded = 40) => {
  if (yPos > 270 - spaceNeeded) {
    doc.addPage();
    return 20;
  }
  return yPos;
};

/**
 * Generar PDF para OrderDetail
 */
export const generateOrderPDF = (order, consolidatedData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // ============================================
  // HEADER
  // ============================================
  try {
    const logo = require('../assets/img/maslogosinletras.png');
    doc.addImage(logo, 'PNG', 15, 10, 30, 15);
  } catch (error) {
    console.warn('Logo no encontrado');
  }

  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('ORDEN DE SERVICIO', pageWidth - 15, 18, { align: 'right' });
  
  doc.setFontSize(13);
  doc.setTextColor(66, 133, 244);
  doc.text(`#${toSafeString(order.order_number)}`, pageWidth - 15, 26, { align: 'right' });
  
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Fecha: ${parseDate(order.created_at).toLocaleDateString('es-MX')}`,
    pageWidth - 15,
    32,
    { align: 'right' }
  );

  y = 42;
  doc.setTextColor(0, 0, 0);

  // ============================================
  // 1. INFORMACIÓN DEL CLIENTE
  // ============================================
  y = addSection(doc, 'INFORMACIÓN DEL CLIENTE', y, pageWidth);
  
  y = addField(doc, 'Cliente:', consolidatedData.clientName, y, pageWidth);
  y = addField(doc, 'Razón Social:', consolidatedData.legalName, y, pageWidth);
  y = addField(doc, 'RFC:', consolidatedData.rfc, y, pageWidth);
  y = addField(doc, 'Contacto:', consolidatedData.contactName, y, pageWidth);
  y = addField(doc, 'Email:', consolidatedData.contactEmail, y, pageWidth);
  y = addField(doc, 'Teléfono:', consolidatedData.contactPhone, y, pageWidth);
  y = addField(doc, 'Dirección:', consolidatedData.address, y, pageWidth);
  
  y += 5;

  // ============================================
  // 2. VENDEDOR Y GESTIÓN
  // ============================================
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'VENDEDOR Y GESTIÓN', y, pageWidth);
  
  y = addField(doc, 'Account Manager:', consolidatedData.sellerName, y, pageWidth);
  y = addField(doc, 'ID Proveedor:', consolidatedData.supplierId, y, pageWidth);
  
  y += 5;

  // ============================================
  // 3. DETALLES DEL SERVICIO
  // ============================================
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'DETALLES DEL SERVICIO', y, pageWidth);
  
  y = addField(doc, 'Categoría:', consolidatedData.category, y, pageWidth);
  y = addField(doc, 'Tipo de Servicio:', consolidatedData.serviceType, y, pageWidth);
  y = addField(doc, 'Horas Totales:', `${toSafeString(consolidatedData.totalHours)} hrs`, y, pageWidth);
  y = addField(doc, 'Sesiones:', consolidatedData.numSessions, y, pageWidth);
  y = addField(doc, 'Personas:', consolidatedData.numPeople, y, pageWidth);
  y = addField(doc, 'Grupos:', consolidatedData.numGroups, y, pageWidth);
  y = addField(doc, 'Idioma:', consolidatedData.language, y, pageWidth);
  y = addField(doc, 'Ciudad:', consolidatedData.cityState, y, pageWidth);
  
  if (consolidatedData.includes) {
    y += 2;
    y = addField(doc, 'Incluye:', consolidatedData.includes, y, pageWidth);
  }
  
  y += 5;

  // ============================================
  // 4. LOGÍSTICA Y ENTREGA
  // ============================================
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'LOGÍSTICA Y ENTREGA', y, pageWidth);
  
  if (consolidatedData.serviceDeliveryDate) {
    y = addField(doc, 'Fecha de Impartición:', 
      parseDate(consolidatedData.serviceDeliveryDate).toLocaleDateString('es-MX'), y, pageWidth);
  }
  
  if (consolidatedData.orderDate) {
    y = addField(doc, 'Fecha de Orden:', 
      parseDate(consolidatedData.orderDate).toLocaleDateString('es-MX'), y, pageWidth);
  }
  
  if (order.facilitator_name || consolidatedData.facilitator !== 'N/A') {
    y = addField(doc, 'Facilitador:', 
      order.facilitator_name || consolidatedData.facilitator, y, pageWidth);
  }
  
  if (order.location || consolidatedData.location) {
    y = addField(doc, 'Lugar:', 
      order.location || consolidatedData.location, y, pageWidth);
  }
  
  y += 5;

  // ============================================
  // 5. DETALLES DEL TALLER
  // ============================================
  const invoiceSchedule = order.invoice_schedule_details;
  
  if (invoiceSchedule?.workshop_name || invoiceSchedule?.entry_requirements || invoiceSchedule?.session_schedule) {
    y = checkPageBreak(doc, y, 50);
    y = addSection(doc, 'DETALLES DEL TALLER', y, pageWidth);
    
    if (invoiceSchedule.workshop_name) {
      y = addField(doc, 'Nombre del Taller:', invoiceSchedule.workshop_name, y, pageWidth);
    }
    
    if (invoiceSchedule.session_schedule) {
      y = addField(doc, 'Horario:', invoiceSchedule.session_schedule, y, pageWidth);
    }
    
    if (invoiceSchedule.program_required !== undefined) {
      y = addField(doc, 'Programa:', 
        invoiceSchedule.program_required ? 'Sí requerido' : 'No requerido', y, pageWidth);
    }
    
    if (invoiceSchedule.diplomas_required !== undefined) {
      y = addField(doc, 'Diplomas:', 
        invoiceSchedule.diplomas_required ? 'Sí requeridos' : 'No requeridos', y, pageWidth);
    }
    
    if (invoiceSchedule.entry_requirements) {
      y += 2;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Requisitos de Ingreso:', 17, y);
      y += 5;
      y = addTextBlock(doc, invoiceSchedule.entry_requirements, y, pageWidth);
    }
    
    y += 5;
  }

  // ============================================
  // 6. NECESIDAD DEL CLIENTE
  // ============================================
  if (order.client_need || consolidatedData.clientNeed) {
    y = checkPageBreak(doc, y, 30);
    y = addSection(doc, 'NECESIDAD DEL CLIENTE', y, pageWidth);
    y = addTextBlock(doc, order.client_need || consolidatedData.clientNeed, y, pageWidth);
    y += 5;
  }

  // ============================================
  // 7. DESCRIPCIÓN DEL SERVICIO
  // ============================================
  if (consolidatedData.description) {
    y = checkPageBreak(doc, y, 30);
    y = addSection(doc, 'DESCRIPCIÓN DEL SERVICIO', y, pageWidth);
    y = addTextBlock(doc, consolidatedData.description, y, pageWidth);
    y += 5;
  }

  // ============================================
  // 8. INFORMACIÓN DE PAGO
  // ============================================
  y = checkPageBreak(doc, y, 40);
  y = addSection(doc, 'INFORMACIÓN DE PAGO', y, pageWidth);
  
  const subtotal = parseFloat(order.subtotal || 0);
  const iva = subtotal * 0.16;
  const total = subtotal + iva;

  // Tabla manual de montos
  doc.setFontSize(9);
  
  // Header
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 7, 'F');
  doc.setFont(undefined, 'bold');
  doc.text('Concepto', 20, y + 4.5);
  doc.text('Monto', pageWidth - 20, y + 4.5, { align: 'right' });
  y += 9;
  
  // Líneas de la tabla
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  
  // Subtotal
  doc.setFont(undefined, 'normal');
  doc.text('Subtotal', 20, y);
  doc.text(`$${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 
    pageWidth - 20, y, { align: 'right' });
  doc.line(15, y + 2, pageWidth - 15, y + 2);
  y += 6;
  
  // IVA
  doc.text('IVA (16%)', 20, y);
  doc.text(`$${iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 
    pageWidth - 20, y, { align: 'right' });
  doc.line(15, y + 2, pageWidth - 15, y + 2);
  y += 6;
  
  // TOTAL (destacado)
  doc.setFillColor(66, 133, 244);
  doc.rect(15, y - 3, pageWidth - 30, 8, 'F');
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL', 20, y + 1.5);
  doc.text(`$${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, 
    pageWidth - 20, y + 1.5, { align: 'right' });
  
  doc.setTextColor(0, 0, 0);
  y += 10;

  // ============================================
  // FOOTER
  // ============================================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generado: ${new Date().toLocaleString('es-MX')}`,
      15,
      doc.internal.pageSize.getHeight() - 10
    );
    doc.text(
      `Sistema MAS+ | www.masarv.com`,
      pageWidth - 15,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'right' }
    );
    doc.text(
      `Página ${i} de ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  const fileName = `Orden_de_Servicio_${order.order_number}_${new Date().toLocaleDateString('es-MX').replace(/\//g, '-')}.pdf`;
  doc.save(fileName);
};

/**
 * Generar PDF para FacilitationDetail (SIN MONTOS)
 */
export const generateFacilitationPDF = (order, consolidatedData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // ============================================
  // HEADER
  // ============================================
  try {
    const logo = require('../assets/img/maslogosinletras.png');
    doc.addImage(logo, 'PNG', 15, 10, 30, 15);
  } catch (error) {
    console.warn('Logo no encontrado');
  }

  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('INFORMACIÓN DEL SERVICIO', pageWidth - 15, 18, { align: 'right' });
  
  doc.setFontSize(13);
  doc.setTextColor(66, 133, 244);
  doc.text(`#${toSafeString(order.order_number)}`, pageWidth - 15, 26, { align: 'right' });
  
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Fecha: ${parseDate(order.created_at).toLocaleDateString('es-MX')}`,
    pageWidth - 15,
    32,
    { align: 'right' }
  );

  y = 42;
  doc.setTextColor(0, 0, 0);

  // ============================================
  // 1. INFORMACIÓN DEL CLIENTE (LIMITADA)
  // ============================================
  y = addSection(doc, 'INFORMACIÓN DEL CLIENTE', y, pageWidth);
  
  y = addField(doc, 'Cliente:', consolidatedData.clientName, y, pageWidth);
  y = addField(doc, 'Contacto:', consolidatedData.contactName, y, pageWidth);
  y = addField(doc, 'Email:', consolidatedData.contactEmail, y, pageWidth);
  y = addField(doc, 'Teléfono:', consolidatedData.contactPhone, y, pageWidth);
  
  y += 5;

  // ============================================
  // 2. DETALLES DEL SERVICIO
  // ============================================
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'DETALLES DEL SERVICIO', y, pageWidth);
  
  y = addField(doc, 'Categoría:', consolidatedData.category, y, pageWidth);
  y = addField(doc, 'Tipo de Servicio:', consolidatedData.serviceType, y, pageWidth);
  y = addField(doc, 'Horas Totales:', `${toSafeString(consolidatedData.totalHours)} hrs`, y, pageWidth);
  y = addField(doc, 'Sesiones:', consolidatedData.numSessions, y, pageWidth);
  y = addField(doc, 'Personas:', consolidatedData.numPeople, y, pageWidth);
  y = addField(doc, 'Grupos:', consolidatedData.numGroups, y, pageWidth);
  y = addField(doc, 'Idioma:', consolidatedData.language, y, pageWidth);
  y = addField(doc, 'Ciudad:', consolidatedData.cityState, y, pageWidth);
  
  y += 5;

  // ============================================
  // 3. LOGÍSTICA Y ENTREGA (DESTACADA)
  // ============================================
  y = checkPageBreak(doc, y);
  
  // Header naranja para destacar
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setFillColor(255, 152, 0);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('LOGÍSTICA Y ENTREGA', 17, y + 5.5);
  doc.setTextColor(0, 0, 0);
  y += 12;
  
  if (consolidatedData.serviceDeliveryDate) {
    y = addField(doc, 'Fecha de Impartición:', 
      parseDate(consolidatedData.serviceDeliveryDate).toLocaleDateString('es-MX'), y, pageWidth);
  }
  
  // Sesiones de facilitación (múltiples o legacy)
  const invoiceSchedule = order.invoice_schedule_details;
  const facilitationSessions = invoiceSchedule?.facilitation_sessions;
  if (facilitationSessions && facilitationSessions.length > 0) {
    facilitationSessions.forEach((session, idx) => {
      const label = facilitationSessions.length > 1 ? `Sesión ${idx + 1}:` : 'Fecha de Impartición:';
      if (session.date) {
        y = addField(doc, label,
          parseDate(session.date + 'T00:00:00').toLocaleDateString('es-MX'), y, pageWidth);
      }
      if (session.facilitator_name) {
        y = addField(doc, 'Facilitador:', session.facilitator_name, y, pageWidth);
      }
      if (session.notes) {
        y = addField(doc, 'Notas sesión:', session.notes, y, pageWidth);
      }
    });
  } else {
    if (consolidatedData.serviceDeliveryDate) {
      y = addField(doc, 'Fecha de Impartición:',
        parseDate(consolidatedData.serviceDeliveryDate).toLocaleDateString('es-MX'), y, pageWidth);
    }
    if (order.facilitator_name || consolidatedData.facilitator !== 'N/A') {
      y = addField(doc, 'Facilitador:',
        order.facilitator_name || consolidatedData.facilitator, y, pageWidth);
    }
  }

  if (order.location || consolidatedData.location) {
    y = addField(doc, 'Lugar:',
      order.location || consolidatedData.location, y, pageWidth);
  }

  y += 5;

  // ============================================
  // 5. NECESIDAD DEL CLIENTE
  // ============================================
  if (order.client_need || consolidatedData.clientNeed) {
    y = checkPageBreak(doc, y, 30);
    y = addSection(doc, 'NECESIDAD DEL CLIENTE', y, pageWidth);
    y = addTextBlock(doc, order.client_need || consolidatedData.clientNeed, y, pageWidth);
    y += 5;
  }
  // ============================================
  // 6. DETALLES DEL TALLER
  // ============================================
  if (invoiceSchedule?.workshop_name || invoiceSchedule?.entry_requirements || invoiceSchedule?.session_schedule) {
    y = checkPageBreak(doc, y, 50);
    y = addSection(doc, 'DETALLES DEL TALLER', y, pageWidth);
    
    if (invoiceSchedule.workshop_name) {
      y = addField(doc, 'Nombre del Taller:', invoiceSchedule.workshop_name, y, pageWidth);
    }
    
    if (invoiceSchedule.session_schedule) {
      y = addField(doc, 'Horario:', invoiceSchedule.session_schedule, y, pageWidth);
    }
    
    if (invoiceSchedule.program_required !== undefined) {
      y = addField(doc, 'Programa:', 
        invoiceSchedule.program_required ? 'Sí requerido' : 'No requerido', y, pageWidth);
    }
    
    if (invoiceSchedule.diplomas_required !== undefined) {
      y = addField(doc, 'Diplomas:', 
        invoiceSchedule.diplomas_required ? 'Sí requeridos' : 'No requeridos', y, pageWidth);
    }
    
    if (invoiceSchedule.entry_requirements) {
      y += 2;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Requisitos de Ingreso:', 17, y);
      y += 5;
      y = addTextBlock(doc, invoiceSchedule.entry_requirements, y, pageWidth);
    }
    
    if (invoiceSchedule.notes) {
      y += 2;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Notas:', 17, y);
      y += 5;
      y = addTextBlock(doc, invoiceSchedule.notes, y, pageWidth);
    }
    
    y += 5;
  }

  // ============================================
  // 6. DESCRIPCIÓN DEL SERVICIO (solo si difiere de client_need)
  // ============================================
  const clientNeedText = order.client_need || consolidatedData.clientNeed || '';
  const descriptionText = consolidatedData.description || '';
  if (descriptionText && descriptionText.trim() !== clientNeedText.trim()) {
    y = checkPageBreak(doc, y, 30);
    y = addSection(doc, 'DESCRIPCIÓN DEL SERVICIO', y, pageWidth);
    y = addTextBlock(doc, descriptionText, y, pageWidth);
    y += 5;
  }

  // ============================================
  // FOOTER
  // ============================================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generado: ${new Date().toLocaleString('es-MX')}`,
      15,
      doc.internal.pageSize.getHeight() - 10
    );
    doc.text(
      `Sistema MAS+ | www.masarv.com`,
      pageWidth - 15,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'right' }
    );
    doc.text(
      `Página ${i} de ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  const fileName = `Servicio_${order.order_number}_${new Date().toLocaleDateString('es-MX').replace(/\//g, '-')}.pdf`;
  doc.save(fileName);
};

/**
 * Generar PDF de información de facturación (desde InvoiceInbox)
 * Incluye todos los campos del panel expandido: cliente, montos, logística,
 * descripción, costos desglosados, contacto y notas.
 */
export const generateInvoiceBillingPDF = (schedule) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  // Extraer sub-objetos igual que en InvoiceInbox
  const quoteItems = schedule.quote_details?.items || {};
  const serviceDetails = quoteItems.service_details || {};
  const clientInfo = quoteItems.client_info || {};
  // Desglose estructurado (cobrado/gastado) derivado de los datos crudos de la cotización.
  const costs = buildCostBreakdown(quoteItems);
  const elaboro = schedule.order_details?.seller_name || schedule.quote_details?.seller_name;

  // ── HEADER ──────────────────────────────────────────────────────────────────
  try {
    const logo = require('../assets/img/maslogosinletras.png');
    doc.addImage(logo, 'PNG', 15, 10, 30, 15);
  } catch (e) {}

  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('INFORMACIÓN PARA FACTURACIÓN', pageWidth - 15, 16, { align: 'right' });

  doc.setFontSize(12);
  doc.setTextColor(66, 133, 244);
  doc.text(toSafeString(schedule.order_number), pageWidth - 15, 24, { align: 'right' });

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Fecha límite: ${parseDate(schedule.scheduled_invoice_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}`,
    pageWidth - 15, 30, { align: 'right' }
  );

  y = 40;
  doc.setTextColor(0, 0, 0);

  // ── 1. DATOS DEL CLIENTE ────────────────────────────────────────────────────
  y = addSection(doc, 'DATOS DEL CLIENTE', y, pageWidth);
  y = addField(doc, 'Nombre:', schedule.billing_name, y, pageWidth);
  y = addField(doc, 'RFC:', schedule.billing_rfc, y, pageWidth);
  y = addField(doc, 'Dirección:', schedule.billing_address, y, pageWidth);
  y += 5;

  // ── 2. MONTOS A FACTURAR ────────────────────────────────────────────────────
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'MONTOS A FACTURAR', y, pageWidth);

  // Se factura el monto de esta parcialidad; el subtotal de la orden solo es respaldo
  // para programaciones antiguas que no tienen monto capturado.
  const scheduledAmount = parseFloat(schedule.scheduled_amount || 0);
  const orderSubtotal = parseFloat(schedule.order_subtotal || 0);
  const subtotal = scheduledAmount > 0 ? scheduledAmount : orderSubtotal;
  const isPartial = scheduledAmount > 0 && orderSubtotal > 0 && scheduledAmount < orderSubtotal;
  const iva = subtotal * 0.16;
  const total = subtotal + iva;

  // Tabla de montos
  doc.setFontSize(9);
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 7, 'F');
  doc.setFont(undefined, 'bold');
  doc.text('Concepto', 20, y + 4.5);
  doc.text('Monto', pageWidth - 20, y + 4.5, { align: 'right' });
  y += 9;
  doc.setFont(undefined, 'normal');
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);

  doc.text('Subtotal (Sin IVA)', 20, y);
  doc.text(fmt(subtotal), pageWidth - 20, y, { align: 'right' });
  doc.line(15, y + 2, pageWidth - 15, y + 2);
  y += 6;

  doc.text('IVA (16%)', 20, y);
  doc.text(fmt(iva), pageWidth - 20, y, { align: 'right' });
  doc.line(15, y + 2, pageWidth - 15, y + 2);
  y += 6;

  doc.setFillColor(66, 133, 244);
  doc.rect(15, y - 3, pageWidth - 30, 8, 'F');
  doc.setFont(undefined, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL', 20, y + 1.5);
  doc.text(fmt(total), pageWidth - 20, y + 1.5, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  y += 10;

  if (isPartial) {
    y = addField(doc, 'Parcialidad:', `Facturación parcial de una orden de ${fmt(orderSubtotal)} (sin IVA)`, y, pageWidth);
  }
  if (elaboro) y = addField(doc, 'Elaboró:', elaboro, y, pageWidth);
  y = addField(doc, 'Días Crédito:', `${schedule.credit_days || 0} días`, y, pageWidth);
  y += 5;

  // ── 3. LOGÍSTICA ────────────────────────────────────────────────────────────
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'LOGÍSTICA', y, pageWidth);
  if (schedule.facilitator_name) y = addField(doc, 'Facilitador:', schedule.facilitator_name, y, pageWidth);
  if (schedule.location)         y = addField(doc, 'Lugar:', schedule.location, y, pageWidth);
  if (schedule.service_delivery_date) {
    y = addField(doc, 'Fecha Inicio:',
      parseDate(schedule.service_delivery_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }),
      y, pageWidth);
  }
  if (schedule.total_hours) y = addField(doc, 'Horas:', `${schedule.total_hours} hrs`, y, pageWidth);
  y += 5;

  // ── 4. DESCRIPCIÓN DEL SERVICIO ─────────────────────────────────────────────
  y = checkPageBreak(doc, y);
  y = addSection(doc, 'DESCRIPCIÓN DEL SERVICIO', y, pageWidth);
  if (schedule.description) y = addField(doc, 'Descripción:', schedule.description, y, pageWidth);
  const serviceName = serviceDetails.service_type || schedule.quote_details?.service_type;
  const category    = serviceDetails.category    || schedule.quote_details?.category;
  if (serviceName)               y = addField(doc, 'Nombre Servicio:', serviceName, y, pageWidth);
  if (category)                  y = addField(doc, 'Categoría:', category, y, pageWidth);
  if (serviceDetails.num_people) y = addField(doc, 'Participantes:', toSafeString(serviceDetails.num_people), y, pageWidth);
  if (serviceDetails.num_groups) y = addField(doc, 'Grupos:', toSafeString(serviceDetails.num_groups), y, pageWidth);
  if (schedule.hourly_rate)      y = addField(doc, 'Precio/Hora:', fmt(schedule.hourly_rate), y, pageWidth);
  y += 5;

  // ── 5. CONTACTO DEL SERVICIO ─────────────────────────────────────────────────
  const contactName  = clientInfo.contact_name  || schedule.quote_details?.contact_name;
  const contactEmail = clientInfo.contact_email || schedule.quote_details?.contact_email;
  if (contactName || contactEmail) {
    y = checkPageBreak(doc, y);
    y = addSection(doc, 'CONTACTO DEL SERVICIO', y, pageWidth);
    if (contactName)  y = addField(doc, 'Nombre:', contactName, y, pageWidth);
    if (contactEmail) y = addField(doc, 'Correo:', contactEmail, y, pageWidth);
    y += 5;
  }

  // ── 6. COSTOS DESGLOSADOS ───────────────────────────────────────────────────
  if (hasAnyCost(costs)) {
    y = checkPageBreak(doc, y, 50);
    y = addSection(doc, 'DESCRIPCIÓN DE LA FACTURA (DESGLOSE DE COSTOS)', y, pageWidth);

    // Encabezado tabla costos
    doc.setFontSize(8);
    doc.setFillColor(240, 240, 240);
    doc.rect(15, y, pageWidth - 30, 6, 'F');
    doc.setFont(undefined, 'bold');
    doc.text('Concepto', 20, y + 4);
    doc.text('Cobrado (sin IVA)', 85, y + 4, { align: 'right' });
    doc.text('Cobrado (con IVA)', 135, y + 4, { align: 'right' });
    doc.text('Gastado', pageWidth - 20, y + 4, { align: 'right' });
    y += 8;
    doc.setFont(undefined, 'normal');

    const costRows = [
      ['VIÁTICOS', costs.viaticos],
      ['LUGAR',    costs.lugar],
      ['MATERIAL', costs.material],
      ['OTRO',     costs.otro],
    ];
    let totCobradoSinIva = 0;
    let totCobradoConIva = 0;
    let totGastado = 0;
    costRows.forEach(([label, c]) => {
      // Ocultar conceptos sin ningún monto (cobrado y gastado en 0)
      if (!conceptHasAmount(c)) return;
      const cobrado = parseFloat(c.cobrado || 0);
      const gastado = parseFloat(c.gastado || 0);
      const sinIva = cobrado / 1.16;
      totCobradoSinIva += sinIva;
      totCobradoConIva += cobrado;
      totGastado += gastado;
      doc.text(label, 20, y);
      doc.text(fmt(sinIva), 85, y, { align: 'right' });
      doc.text(fmt(cobrado), 135, y, { align: 'right' });
      doc.text(fmt(gastado), pageWidth - 20, y, { align: 'right' });
      doc.setDrawColor(220, 220, 220);
      doc.line(15, y + 2, pageWidth - 15, y + 2);
      y += 6;
    });

    // Fila TOTAL
    doc.setFont(undefined, 'bold');
    doc.text('TOTAL', 20, y);
    doc.text(fmt(totCobradoSinIva), 85, y, { align: 'right' });
    doc.text(fmt(totCobradoConIva), 135, y, { align: 'right' });
    doc.text(fmt(totGastado), pageWidth - 20, y, { align: 'right' });
    doc.setDrawColor(66, 133, 244);
    doc.setLineWidth(0.5);
    doc.line(15, y + 2, pageWidth - 15, y + 2);
    doc.setLineWidth(0.3);
    doc.setFont(undefined, 'normal');
    y += 6;
    y += 3;
  }

  // ── 7. NOTAS ─────────────────────────────────────────────────────────────────
  if (schedule.notes) {
    y = checkPageBreak(doc, y, 30);
    y = addSection(doc, 'NOTAS', y, pageWidth);
    y = addTextBlock(doc, schedule.notes, y, pageWidth);
    y += 5;
  }

  // ── FOOTER ───────────────────────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generado: ${new Date().toLocaleString('es-MX')}`, 15, doc.internal.pageSize.getHeight() - 10);
    doc.text(`Sistema MAS+ | www.masarv.com`, pageWidth - 15, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
    doc.text(`Página ${i} de ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }

  doc.save(`Info_Facturacion_${schedule.order_number}_${new Date().toLocaleDateString('es-MX').replace(/\//g, '-')}.pdf`);
};
